import { GrStack, GrTypography } from "@jnpmedi/grebob";
import { resolvePrefix, resolveSuffix } from "@newLibs/converting";
import { MvnMessage } from "@newLibs/utils";
import { AuditTrailViewType } from "./SignerAuditTrail";
import { DOCS_DOCUMENT_LANG } from "@maven-msg/lang/docs";
import { DocumentSignerHistory, DocumentSignerHistoryType } from "@maven-rest/docs/DocumentSign.dto";
import { ellipsis } from "@maven-helper/functions/Misc.function";
import dayjs from "dayjs";

interface AuditTrailRowProps {
  history: DocumentSignerHistory;
  viewType?: AuditTrailViewType;
}

export function AuditTrailRow({ history, viewType = AuditTrailViewType.Detail }: AuditTrailRowProps) {
  const signer = history.signer;
  const isLeftSizeViewMode = viewType === AuditTrailViewType.LeftSide;

  const { email, name, title } = history.referrer
    ? { email: history.referrer.email, name: history.referrer.name, title: history.referrer.title }
    : history.actantId
    ? { email: history.actantEmail, name: history.actantName, title: null }
    : { email: signer?.email, name: signer?.name, title: signer?.title };

  const isSignerNotExists = !name && !title && !email;
  const isSignerExists = !!name || !!title || !email;

  const isReasonDisplayable = [DocumentSignerHistoryType.Signed, DocumentSignerHistoryType.Canceled].includes(history.type);
  const isActantShouldNotVisible = [
    DocumentSignerHistoryType.SignEnd,
    DocumentSignerHistoryType.SignDecline,
    DocumentSignerHistoryType.SignCancel,
    DocumentSignerHistoryType.SignExpired,
  ].includes(history.type);

  const renderReason = (): React.ReactElement | null => {
    if (history.type === DocumentSignerHistoryType.Signed) {
      return <>{!!history.reason && `(${MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_REASON)} ${history.reason})`}</>;
    } else if (history.type === DocumentSignerHistoryType.Canceled) {
      return (
        <>
          {!!history.reason && (
            <>
              &nbsp;
              <b>{MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_CANCEL_REASON_PREFIX)}</b>
              {history.reason}
              <b>{MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_CANCEL_REASON_SUFFIX)}</b>
            </>
          )}
        </>
      );
    }

    return null;
  };

  return (
    <>
      {isSignerExists && (
        <GrStack
          flexDirection={isLeftSizeViewMode ? "column" : "row"}
          justifyContent={isLeftSizeViewMode ? undefined : "space-between"}
          alignItems={isLeftSizeViewMode ? undefined : "center"}
          width="100%"
          gap={8}
        >
          <p style={{ margin: 0 }}>
            {MvnMessage(resolvePrefix(history.type))}&nbsp;
            {!isActantShouldNotVisible && (
              <b>
                <i> {!!history.referrer && MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_STATUS_REFERRER_LABEL).toLowerCase() + "."} </i>
                {ellipsis(name || "", 48)} ({ellipsis(email || "", 32)})&nbsp;
              </b>
            )}
            {MvnMessage(resolveSuffix(history.type))}
            {isReasonDisplayable && renderReason()}
          </p>
          <GrTypography type={isLeftSizeViewMode ? "Content.Body04" : "Content.Body03"} color="grey050" style={{ minWidth: "fit-content" }}>
            {dayjs(history.createdAt).format("YYYY/MM/DD HH:mm:ss Z")}
          </GrTypography>
        </GrStack>
      )}
      {!isActantShouldNotVisible && isSignerNotExists && (
        <strong>
          {MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_NTH_SIGNER_LABEL, {
            signerIndex: signer?.signerIndex || "-",
          })}
        </strong>
      )}
    </>
  );
}
